import { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { Paper, Chip, Button, ButtonGroup } from '@mui/material';
import { Edit as EditIcon, History as HistoryIcon } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { frFR, enUS } from '@mui/x-data-grid/locales';
import { formatDateTime } from '@utils/helpers';
import API from '@/utils/axios';
import theme from '@/theme';
import { useAuth } from '@contexts/AuthContext';
import useLocalStorage from '@/hooks/useLocalStorage';
import OurPagination from '@components/OurPagination';
import OurDataToolbar from '@components/OurDataToolbar';
import PackTypeIcon from '@components/pack/PackTypeIcon';

const PackTable = forwardRef(({ customerId, type, onEdit=null, onHistory=null, ...props }, ref) => {
    const { t, i18n } = useTranslation();
    const { doLogout } = useAuth();
    const [loading, setLoading] = useState(true);
    const [packs, setPacks] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [paginationModel, setPaginationModel] = useLocalStorage('packs.pagination', {
        pageSize: 10,
        page: 0,
    });
    const [sortModel, setSortModel] = useLocalStorage('packs.sort', [{
        field: 'created_at',
        sort: 'desc',
    }]);
    const [columnVisibility, setColumnVisibility] = useLocalStorage('packs.columns', {
        quantity: false,
        used_quantity: false,
        remaining_quantity: false,
        updated_at: false,
    });
    const [density, setDensity] = useLocalStorage('packs.density', 'standard');

    const loadPacks = useCallback(async (paginationModel, sortModel) => {
        try {
            setLoading(true);
            const { field = '', sort = '' } = sortModel[0] || {};
            let params = {
                type: type,
                per_page: paginationModel.pageSize,
                page: paginationModel.page + 1,
                sort: field,
                direction: sort,
            };
            const response = await API.get(`/customers/${customerId}/packs`, { params });
            setPacks(
                response.data.data.map((pack) => ({
                    id: pack.id,
                    type: pack.type,
                    quantity: pack.quantity,
                    used_quantity: pack.used_quantity,
                    created_at: new Date(pack.created_at),
                    updated_at: new Date(pack.updated_at),
                }))
            );
            setRowCount(response.data.total);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            if (error.response && error.response.status === 401) {
                doLogout();
            } else {
                console.error('Erreur de chargement', error);
            }
        }
    }, [doLogout, customerId, type]);

    useEffect(() => {
        loadPacks(paginationModel, sortModel);
    }, [loadPacks, paginationModel, sortModel]);

    useImperativeHandle(ref, () => ({
        refresh: () => loadPacks(paginationModel, sortModel)
    }));

    const localeTheme = i18n.language === 'fr' ? frFR : enUS;
    const themeWithLocale = createTheme(theme, localeTheme);

    const columns = [
        {
            field: 'id',
            headerName: t('ID'),
            flex: 0.5,
            type: 'number',
            maxWidth: 70,
        },
        {
            field: 'created_at',
            headerName: t('Creation date'),
            flex: 1,
            type: 'dateTime',
            valueFormatter: formatDateTime
        },
        {
            field: 'updated_at',
            headerName: t('Update date'),
            flex: 1,
            type: 'dateTime',
            valueFormatter: formatDateTime
        },
        {
            field: 'quantity',
            headerName: t('Quantity'),
            flex: 1,
            type: 'number'
        },
        {
            field: 'used_quantity',
            headerName: t('Used quantity'),
            flex: 1,
            type: 'number',
        },
        {
            field: 'remaining_quantity',
            headerName: t('Remaining quantity'),
            flex: 1,
            type: 'number',
            sortable: false,
            valueGetter: (value, row) => row.quantity - row.used_quantity,
        },
        {
            field: 'details_quantity',
            headerName: t('Remaining / total quantity'),
            flex: 1,
            type: 'string',
            sortable: false,
            renderCell: function DetailsQuantityCell(params) {
                const remaining = params.row.quantity - params.row.used_quantity;
                let color = 'success';
                if (remaining === 0) {
                    color = null;
                } else if (remaining < 0) {
                    color = 'error';
                }
                return <Chip {...(color && { color })} 
                    icon={<PackTypeIcon type={params.row.type} sx={{ ml: 1 }} fontSize="small" />}
                    label={`${remaining} / ${params.row.quantity}`} />
            }
        },
        ...(onEdit !== null || onHistory !== null ? [
            {
                field: 'actions',
                headerName: t('Actions'),
                flex: 1,
                sortable: false,
                disableColumnMenu: true,
                renderCell: function (params) {
                    return <ButtonGroup size="small" variant="contained" sx={{ mt: 1 }}>
                        {onEdit !== null && <Button startIcon={<EditIcon />} onClick={() => onEdit(params.row)}>{t('Edit')}</Button>}
                        {onHistory !== null && <Button startIcon={<HistoryIcon />} onClick={() => onHistory(params.row)}>{t('History')}</Button>}
                    </ButtonGroup>;
                }
            }
        ] : []),
    ];

    return (
        <ThemeProvider theme={themeWithLocale}>
            <Paper>
                <DataGrid
                    autoHeight
                    loading={loading}
                    slots={{
                        toolbar: OurDataToolbar,
                        pagination: OurPagination,
                    }}
                    slotProps={{
                        toolbar: { search: false },
                    }}
                    initialState={{
                        sorting: { sortModel }
                    }}
                    rows={packs}
                    columns={columns}
                    pageSizeOptions={[10, 15, 20, 50, 100]}
                    pagination
                    paginationMode="server"
                    rowCount={rowCount}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    columnVisibilityModel={columnVisibility}
                    onColumnVisibilityModelChange={(newModel) =>
                        setColumnVisibility(newModel)
                    }
                    sx={{
                        '& .MuiDataGrid-row': {
                            cursor: 'default'
                        }
                    }}
                    disableRowSelectionOnClick={true}
                    disableSelectionOnClick={true}
                    sortingMode="server"
                    sortingOrder={['asc', 'desc']}
                    onSortModelChange={setSortModel}
                    density={density}
                    onDensityChange={setDensity}
                    disableColumnResize={true}
                    disableColumnFilter
                />
            </Paper>
        </ThemeProvider>
    );
});

export default PackTable;